// src/components/Footer.js

import React, { useState } from 'react'
import { supabase } from '../lib/supabaseClient'
import PrivacyModal from './PrivacyModal'
import TermsModal from './TermsModal'
import { EnvelopeIcon } from '@heroicons/react/24/outline'

export default function Footer() {
  const [newsletterEmail, setNewsletterEmail] = useState('')
  const [subscribed, setSubscribed] = useState(false)
  const [showPrivacy, setShowPrivacy] = useState(false)
  const [showTerms, setShowTerms] = useState(false)
  const [showSubscribeConfirm, setShowSubscribeConfirm] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const handleSubscribe = (e) => {
    e.preventDefault()
    setError(null)
    setShowSubscribeConfirm(true)
  }

  const confirmSubscribe = async () => {
    setShowSubscribeConfirm(false)
    setLoading(true)
    setError(null)

    const { error: insertError } = await supabase
      .from('newsletter')
      .insert([{ email: newsletterEmail }])

    setLoading(false)
    if (insertError) {
      setError('Subscription failed. Please try again.')
    } else {
      setSubscribed(true)
      setNewsletterEmail('')
    }
  }

  const cancelSubscribe = () => {
    setShowSubscribeConfirm(false)
  }

  return (
    <>
      <footer className="bg-[#fdf5ef] border-t border-gray-200 pt-8 pb-6">
        <div className="max-w-7xl mx-auto px-6 md:px-20">
          {/* Quick Links */}
          <nav className="flex flex-wrap justify-center gap-6 text-sm mb-8">
            {[
              { label: 'Home', to: '/' },
              { label: 'Explore Vendors', to: '/vendors' },
              { label: 'Become a Vendor', to: '/become-a-vendor' },
              { label: 'About', to: '/about' },
              { label: 'Contact', to: '/contact' },
            ].map(({ label, to }) => (
              <a
                key={to}
                href={to}
                className="hover:text-orange-600 transition-colors"
              >
                {label}
              </a>
            ))}
            <button
              onClick={() => setShowPrivacy(true)}
              className="hover:text-orange-600 transition-colors"
            >
              Privacy Policy
            </button>
            <button
              onClick={() => setShowTerms(true)}
              className="hover:text-orange-600 transition-colors"
            >
              Terms of Service
            </button>
          </nav>

          {/* Social + Email Icons */}
          <div className="flex justify-center items-center gap-6 mb-8">
            <a
              href="https://www.facebook.com/profile.php?id=61575412142365"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Facebook"
              className="hover:opacity-80 transition-opacity"
            >
              <img src="/assets/fb.png" alt="" className="w-6 h-6" />
            </a>
            <a
              href="https://instagram.com/officialmyeventmitra"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Instagram"
              className="hover:opacity-80 transition-opacity"
            >
              <img src="/assets/insta.png" alt="" className="w-6 h-6" />
            </a>
            <a
              href="mailto:info@myeventmitra.com"
              aria-label="Email Us"
              className="hover:text-orange-600 transition-colors"
            >
              <EnvelopeIcon className="w-6 h-6 text-orange-500" />
            </a>
          </div>

          {/* Newsletter Signup */}
          <div className="flex justify-center mb-8">
            {!subscribed ? (
              <form
                onSubmit={handleSubscribe}
                className="bg-white p-6 rounded-lg shadow-md flex flex-col sm:flex-row items-center gap-2 max-w-md w-full"
              >
                <input
                  type="email"
                  required
                  placeholder="Your email"
                  value={newsletterEmail}
                  onChange={e => setNewsletterEmail(e.target.value)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-orange-500"
                />
                <button
                  type="submit"
                  disabled={loading}
                  className="bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded-md transition-opacity disabled:opacity-50"
                >
                  {loading ? 'Submitting...' : 'Subscribe'}
                </button>
              </form>
            ) : (
              <div className="text-green-600 font-semibold">
                Thanks for subscribing!
              </div>
            )}
          </div>
          {error && (
            <p className="text-center text-red-600 mb-8">{error}</p>
          )}

          {/* Back to Top */}
          <div className="text-center mb-8">
            <a
              href="#top"
              className="text-sm hover:text-orange-600 transition-colors"
            >
              ↑ Back to top
            </a>
          </div>

          {/* Copyright */}
          <p className="text-center text-xs text-gray-500">
            © 2025 MyEventMitra. All rights reserved.
          </p>
        </div>
      </footer>

      {/* Privacy & Terms Modals */}
      {showPrivacy && <PrivacyModal onClose={() => setShowPrivacy(false)} />}
      {showTerms   && <TermsModal   onClose={() => setShowTerms(false)}   />}

      {/* Subscription Confirmation Modal */}
      {showSubscribeConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-sm w-full space-y-4 shadow-lg">
            <h2 className="text-xl font-bold">Confirm Subscription</h2>
            <p className="text-gray-700">
              We will collect your email to send you occasional updates and promotional offers.
              Do you agree to receive these emails?
            </p>
            <div className="flex justify-end gap-2 mt-4">
              <button
                onClick={cancelSubscribe}
                className="px-4 py-2 rounded-md border border-gray-300"
              >
                Cancel
              </button>
              <button
                onClick={confirmSubscribe}
                className="px-4 py-2 bg-orange-500 text-white rounded-md"
              >
                I Agree
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}